import React, { useState, useCallback, useRef, useEffect } from 'react';
import StandardMenuContent, {
  StandardMenuContentProps,
} from '../StandardMenuContent';
import { usePreviousValue } from '@wix/da-hooks/pkg/usePreviousValue';

import classnames from 'classnames';
import ScrollableContent from '../../../../ScrollableContent';
import ArrowUp from '../../../../Icons/ArrowUp';
import ArrowDown from '../../../../Icons/ArrowDown';
import s from './ScrollableMenuContent.scss';

export interface Props extends StandardMenuContentProps {
  label?: React.ReactNode;
  labelClassName?: string;
  withScrollableArrows?: boolean;
  onScrollToLast?: () => void;
  onScrollToFirst?: () => void;
}

const ScrollableMenuContent: React.FC<Props> = ({
  label,
  labelClassName,
  withScrollableArrows,
  onScrollToLast,
  onScrollToFirst,
  ...contentProps
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [distanceToBottom, setDistanceToBottom] = useState(0);
  const prevScrollTop = usePreviousValue(scrollTop);
  const prevDistanceToBottom = usePreviousValue(distanceToBottom);
  const scrollableRef = useRef<HTMLElement | null>(null);

  const setScrollableRef = useCallback((node: HTMLElement | null) => {
    scrollableRef.current = node;
  }, []);

  const onScrolled = useCallback((top: number, bottom: number) => {
    setScrollTop(top);
    setDistanceToBottom(bottom);
  }, []);

  const onScrollClick = useCallback((e: React.MouseEvent, distance: number) => {
    if (!scrollableRef.current) {
      return;
    }

    scrollableRef.current.scrollBy({ top: distance, behavior: 'smooth' });
    e.stopPropagation();
  }, []);

  useEffect(() => {
    if (
      distanceToBottom !== prevDistanceToBottom &&
      distanceToBottom === 0 &&
      onScrollToLast
    ) {
      onScrollToLast();
    }
    if (scrollTop !== prevScrollTop && scrollTop === 0 && onScrollToFirst) {
      onScrollToFirst();
    }
  }, [
    prevDistanceToBottom,
    distanceToBottom,
    onScrollToLast,
    prevScrollTop,
    scrollTop,
    onScrollToFirst,
  ]);

  return (
    <>
      {label && (
        <div
          className={classnames(
            s['label'],
            labelClassName,
            scrollTop > 0 && s['sticky']
          )}
        >
          {label}
        </div>
      )}
      <ScrollableContent
        className={classnames(
          s['scrollable-content'],
          scrollTop === 0 && s['top'],
          distanceToBottom === 0 && s['bottom']
        )}
        onScrolled={onScrolled}
        innerRef={setScrollableRef}
      >
        <StandardMenuContent {...contentProps}>
          {withScrollableArrows && (
            <>
              <div
                className={s['scroll-arrow-up']}
                onClick={e => onScrollClick(e, -200)}
              >
                <div className={s['scroll-arrow-clickable']}>
                  <ArrowUp disableDefaultColors />
                </div>
              </div>
              <div
                className={s['scroll-arrow-down']}
                onClick={e => onScrollClick(e, 200)}
              >
                <div className={s['scroll-arrow-clickable']}>
                  <ArrowDown disableDefaultColors />
                </div>
              </div>
            </>
          )}
        </StandardMenuContent>
      </ScrollableContent>
    </>
  );
};
ScrollableMenuContent.displayName = 'ScrollableMenuContent';

export default ScrollableMenuContent;
